/**
 * Client wrappers for the NYC Bridge platform callables
 * (suite-io/functions/src/handlers/bridge/). Same shape as
 * handheldCallables.js. Errors propagate as Firebase HttpsError; callers
 * read err.code ("not-found", "already-exists", "permission-denied").
 *
 * requestPairing/pollPairing are NOT wrapped here — only the bridge exe
 * calls those; the Suite UI claims and revokes.
 */
import { callable } from "../firebase.js";

const call = async (name, payload) => {
  const fn = callable(name);
  const result = await fn(payload);
  return result.data;
};

export const bridge = {
  /**
   * Claim the code shown on a new machine's screen. capabilities is a
   * non-empty array from BRIDGE_CAPABILITIES; assignedJobs may be empty
   * (paired but job-less until assigned). Returns { uid, label }.
   */
  claimPairingCode: ({ code, label, capabilities, assignedJobs = [], pools }) =>
    call("claimPairingCode", { code, label, capabilities, assignedJobs, ...(pools ? { pools } : {}) }),

  /**
   * Slice 2.2, admin-only and audited. Change a paired machine's capabilities,
   * jobs and pools without re-pairing (each optional; a named list replaces
   * the machine's). Returns { ok, capabilities, assignedJobs, pools, claimsSynced }.
   */
  setMachineAccess: ({ uid, capabilities, assignedJobs, pools }) =>
    call("setMachineAccess", {
      uid,
      ...(capabilities ? { capabilities } : {}),
      ...(assignedJobs ? { assignedJobs } : {}),
      ...(pools ? { pools } : {}),
    }),

  /** An admin's score (0..100) or "never" for one work kind; override null clears it. */
  setSuitabilityOverride: ({ uid, kind, override }) => call("setSuitabilityOverride", { uid, kind, override }),

  /**
   * "Confirm this root": bind one machine's one root to a place, against the
   * facts the admin looked at. place is { placeId } or { newPlace: { kind, label } }.
   */
  confirmStoragePlace: ({ uid, rootName, facts, place }) => call("confirmStoragePlace", { uid, rootName, facts, place }),

  /** Every place and binding (active and retired). Returns { places, bindings }. */
  listStoragePlaces: () => call("listStoragePlaces", {}),

  /** The open "Needs you" rows, oldest first. Returns { rows }. */
  listAttention: () => call("listAttention", {}),

  /**
   * Slice 5.2, admin-only: the Transfers projection for one job, organization
   * or capture ({ jobId } | { orgId } | { sn }). Returns { copies, legacy, orders, places }.
   */
  getFileCopies: (by) => call("getFileCopies", by),

  /** Kill a machine's credential. Idempotent. Returns { ok: true }. */
  revokeMachine: ({ uid }) => call("revokeMachine", { uid }),

  /**
   * Current published bridge release. No auth required.
   * Returns { version, url, sha256 } or null when no release exists.
   */
  getBridgeRelease: () => call("getBridgeRelease", {}),
};
